import { useFocusEffect, useNavigation } from 'expo-router';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { FlatList, StyleSheet, useWindowDimensions, View } from 'react-native';

import { useGetFavorites } from '@/api/hooks/favoritesHooks';
import GoBackButton from '@/components/buttons/GoBackButton';
import DotsLoader from '@/components/DotsLoader';
import ListEmptyComponent from '@/components/ListEmptyComponent';
import NextPageFetchingIndicator from '@/components/NextPageFetchingIndicator';
import FavoriteCard from '@/components/settings/favorites/FavoriteCard';
import Title from '@/components/Title';
import { extractFavoriteDataItem } from '@/utils/favoriteLogic';
import { i18n } from '@/utils/i18n';

const Favorites = () => {
  const { width } = useWindowDimensions();
  const navigation = useNavigation();

  const [numOfColumns, setNumOfColumns] = useState(3);

  useEffect(() => {
    if (width > 1200 && numOfColumns !== 3) {
      setNumOfColumns(3);
    } else if (width > 820 && width < 1200 && numOfColumns !== 2) {
      setNumOfColumns(2);
    } else if (width < 820 && numOfColumns !== 1) {
      setNumOfColumns(1);
    }
  }, [numOfColumns, width]);

  //Locales
  const favorites = i18n.t('settings.screen_names.favorites');
  const empty_favorites = i18n.t('favorites.empty_favorites');

  const { data, isPending, isFetching, refetch, hasNextPage, isFetchingNextPage, isRefetching, fetchNextPage } =
    useGetFavorites({
      n: '0',
      limit: 10
    });

  const favoritesItems = data?.pages.flatMap((page) => page.results);

  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => <Title label={favorites} />,
      headerLeft: () => <GoBackButton />
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useFocusEffect(
    useCallback(() => {
      refetch();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
  );

  const renderList = () => (
    <FlatList
      //Needed to force refresh of columns on the FlatList
      key={numOfColumns}
      {...(numOfColumns > 1 && {
        columnWrapperStyle: styles.flatListColumnWrapperStyle
      })}
      style={styles.flatList}
      directionalLockEnabled
      contentContainerStyle={styles.flatListContentContainerStyle}
      numColumns={numOfColumns}
      keyExtractor={(item) => item.id}
      data={favoritesItems}
      showsVerticalScrollIndicator={false}
      showsHorizontalScrollIndicator={false}
      onRefresh={refetch}
      refreshing={isRefetching}
      onEndReachedThreshold={0.2}
      onEndReached={(_) => {
        hasNextPage && !isFetching && fetchNextPage();
      }}
      renderItem={({ item }) => {
        const favoriteData = extractFavoriteDataItem(item);

        if (!favoriteData) return null;

        const { title, image, onPress } = favoriteData;

        return (
          <View style={{ flex: 1 / numOfColumns }}>
            <FavoriteCard title={title} image={image} onPress={onPress ? onPress : () => undefined} />
          </View>
        );
      }}
      ListEmptyComponent={<ListEmptyComponent label={empty_favorites} />}
      ListFooterComponent={isFetchingNextPage ? <NextPageFetchingIndicator /> : null}
    />
  );

  return <View style={styles.container}>{isPending ? <DotsLoader /> : renderList()}</View>;
};

export default Favorites;

const styles = StyleSheet.create({
  flatList: {
    marginTop: 10,
    width: '100%'
  },

  container: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 18,
    height: '100%'
  },

  //Vertical Gap for flatList
  flatListContentContainerStyle: {
    flex: 1,
    gap: 27
  },

  //Horizontal Gap for flatList
  flatListColumnWrapperStyle: {
    flex: 1,
    gap: 22
  }
});
